import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productsAPI, commentsAPI, publicUploadAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const maxCommentPhotos = 3;
const maxSize = 2 * 1024 * 1024; // 2MB

export const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Comment form
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState('');
  const [photoUrls, setPhotoUrls] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const p = await productsAPI.getById(id);
      setProduct(p);
      const cs = await commentsAPI.list(id);
      setComments(cs);
    } catch (e) {
      setError('Erreur de chargement du produit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const onUploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      setUploading(true);
      const next = [...photoUrls];
      for (const file of files) {
        if (next.length >= maxCommentPhotos) break;
        if (file.size > maxSize) {
          alert('Image trop volumineuse (max 2 Mo)');
          continue;
        }
        const { url } = await publicUploadAPI.uploadImage(file);
        next.push(url);
      }
      setPhotoUrls(next);
    } catch (err) {
      alert("Upload de photo échoué");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (url) => {
    setPhotoUrls(prev => prev.filter(u => u !== url));
  };

  const submitComment = async (e) => {
    e.preventDefault();
    try {
      if (!name.trim() || !message.trim()) {
        alert('Nom et message requis');
        return;
      }
      await commentsAPI.create({
        product_id: id,
        name,
        message,
        rating: rating ? Number(rating) : undefined,
        photos: photoUrls,
      });
      setName('');
      setMessage('');
      setRating('');
      setPhotoUrls([]);
      alert('Merci pour votre avis ! Il sera visible après approbation.');
      await load();
    } catch (err) {
      alert("Échec de l'envoi du commentaire");
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-16">Chargement...</div>;
  }
  if (error || !product) {
    return <div className="container mx-auto px-4 py-16 text-red-600">{error || 'Produit introuvable'}</div>;
  }

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">← Retour</Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12">
          {/* Gallery */}
          <div>
            <img src={product.image_base64 || ((product.images_base64 && product.images_base64[0]) ? product.images_base64[0] : (product.images && product.images[0]) || product.image)} alt={product.name} className="w-full h-80 object-contain bg-white border rounded" />
            {((product.images_base64 && product.images_base64.length > 1) || (product.images && product.images.length > 1)) && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {(product.images_base64 ? product.images_base64.slice(1, 5) : product.images ? product.images.slice(1, 5) : []).map((url, idx) => (
                  <img key={idx} src={url} alt={`${product.name} ${idx+2}`} className="w-full h-20 object-contain bg-white border rounded" />
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
            <p className="text-gray-700 mb-4">{product.description}</p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1 mb-4">
              {(product.specs || []).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
            <div className="text-xl font-semibold text-yellow-700 mb-6">{product.price}</div>
            <Button onClick={() => {
              window.location.href = '/#contact';
            }} className="bg-yellow-600 text-black">Demander un devis</Button>
          </div>
        </div>

        {/* Comments list */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-4">Avis clients</h2>
          {comments.length === 0 ? (
            <p className="text-gray-600">Aucun avis pour le moment.</p>
          ) : (
            <div className="space-y-4">
              {comments.map(c => (
                <div key={c.id} className="border rounded p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-gray-900">{c.name}</div>
                    {c.rating && <div className="text-yellow-600">{`★`.repeat(c.rating)}{`☆`.repeat(5-c.rating)}</div>}
                  </div>
                  <div className="text-gray-700 mb-2">{c.message}</div>
                  {Array.isArray(c.photos) && c.photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {c.photos.map((u, i) => (<img key={i} src={u} alt={`photo-${i}`} className="w-full h-24 object-cover rounded" />))}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-2">En attente d'approbation: {c.approved ? 'Non' : 'Oui'}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comment form */}
        <div className="bg-gray-50 border rounded p-6">
          <h3 className="text-lg font-semibold mb-4">Laisser un commentaire</h3>
          <form onSubmit={submitComment} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Nom</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Message</label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Note (1 à 5) - optionnel</label>
              <Input type="number" min="1" max="5" value={rating} onChange={(e) => setRating(e.target.value)} placeholder="ex: 5" />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Photos (max {maxCommentPhotos}, 2 Mo chacune) - optionnel</label>
              <input type="file" accept="image/*" multiple onChange={onUploadPhotos} disabled={uploading || photoUrls.length >= maxCommentPhotos} />
              {photoUrls.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {photoUrls.map((u) => (
                    <div key={u} className="relative">
                      <img src={u} alt="apercu" className="w-20 h-20 object-cover rounded border" />
                      <button type="button" onClick={() => removePhoto(u)} className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full px-1">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button type="submit" className="bg-yellow-600 text-black" disabled={uploading}>Envoyer</Button>
            <p className="text-xs text-gray-500">Votre avis sera visible après validation par notre équipe.</p>
          </form>
        </div>
      </div>
    </section>
  );
};